import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Navbar from './components/Navbar';
import Hero from './components/Hero';
import MarqueeBar from './components/MarqueeBar';
import Services from './components/Services';
import WhyUs from './components/WhyUs';
import Gallery from './components/Gallery';
import Testimonials from './components/Testimonials';
import Booking from './components/Booking';
import Footer from './components/Footer';

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/* Page-load overlay */
function LoadingScreen({ done }) {
  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #3d1528 50%, #6B2737 100%)' }}
        >
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center gap-4"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-roseGold to-burgundy flex items-center justify-center shadow-2xl shadow-roseGold/40">
              <svg viewBox="0 0 60 60" fill="none" className="w-8 h-8">
                <circle cx="14" cy="14" r="10" stroke="#F5E6D3" strokeWidth="2" fill="none" opacity="0.8" />
                <circle cx="46" cy="14" r="10" stroke="#F5E6D3" strokeWidth="2" fill="none" opacity="0.8" />
                <line x1="21" y1="21" x2="39" y2="39" stroke="#F5E6D3" strokeWidth="2.5" strokeLinecap="round" />
                <line x1="39" y1="21" x2="21" y2="39" stroke="#F5E6D3" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-playfair text-2xl font-bold text-champagne tracking-wide">Lamisking</p>
              <p className="font-inter text-roseGold text-xs tracking-[0.3em] uppercase mt-1">Hair Salon &amp; Spa</p>
            </div>
            {/* Loading bar */}
            <motion.div
              className="w-32 h-0.5 bg-white/10 rounded-full overflow-hidden mt-2"
            >
              <motion.div
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 1, ease: 'easeInOut' }}
                className="h-full bg-gradient-to-r from-roseGold to-champagne rounded-full"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* Back-to-top button */
function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.7 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-28 right-5 z-40 w-10 h-10 rounded-full bg-white border border-champagne shadow-lg shadow-roseGold/15 flex items-center justify-center text-roseGold hover:bg-roseGold hover:text-white hover:border-roseGold transition-all duration-300"
          aria-label="Back to top"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </motion.button>
      )}
    </AnimatePresence>
  );
}

export default function App() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 1400);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {/* Page-load splash */}
      <LoadingScreen done={loaded} />

      {/* Main site */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={loaded ? { opacity: 1 } : {}}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Navbar />
        <main>
          <Hero />
          <MarqueeBar />
          <Services />
          <WhyUs />
          <Gallery />
          <Testimonials />
          <Booking />
        </main>
        <Footer />
      </motion.div>

      {/* WhatsApp floating action button */}
      <motion.a
        href="https://wa.me/2349018287564"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        initial={{ scale: 0, opacity: 0 }}
        animate={loaded ? { scale: 1, opacity: 1 } : {}}
        transition={{ type: 'spring', delay: 2, damping: 14, stiffness: 200 }}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-xl shadow-green-500/40 hover:shadow-green-500/60 hover:scale-110 transition-transform duration-300"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <WhatsAppIcon />
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-25" />
      </motion.a>

      {/* Back to top */}
      <BackToTop />
    </>
  );
}
